import { useCallback, useState } from "react";
import { api, HttpError } from "../lib/api";
import ChatTab from "./ChatTab";
import ImageTab from "./ImageTab";
import VideoTab from "./VideoTab";
import LibraryTab from "./LibraryTab";

export type TabId = "chat" | "image" | "video" | "library";

export interface VideoSeed {
  kind: "text" | "image" | "extend";
  prompt?: string;
  sourceImageId?: string;
  sourceImageUrl?: string;
  sourceVideoId?: string;
  sourceVideoUrl?: string;
}

export default function Portal({ onUnauthorized }: { onUnauthorized: () => void }) {
  const [tab, setTab] = useState<TabId>("chat");
  const [imagePromptSeed, setImagePromptSeed] = useState<string | null>(null);
  const [videoSeed, setVideoSeed] = useState<VideoSeed | null>(null);

  const handleErr = useCallback(
    (e: unknown) => {
      if (e instanceof HttpError && e.status === 401) onUnauthorized();
    },
    [onUnauthorized],
  );

  function jumpToImage(prompt: string) {
    setImagePromptSeed(prompt);
    setTab("image");
  }

  function jumpToVideo(seed: VideoSeed) {
    setVideoSeed(seed);
    setTab("video");
  }

  async function logout() {
    try {
      await api.logout();
    } finally {
      onUnauthorized();
    }
  }

  return (
    <div className="h-full flex flex-col">
      <header className="border-b border-zinc-900 px-4 py-2 flex items-center gap-1">
        <span className="font-semibold tracking-tight mr-3">Groky</span>
        <nav className="flex gap-1">
          <button data-active={tab === "chat"} className="tab-btn" onClick={() => setTab("chat")}>
            Chat
          </button>
          <button data-active={tab === "image"} className="tab-btn" onClick={() => setTab("image")}>
            Image
          </button>
          <button data-active={tab === "video"} className="tab-btn" onClick={() => setTab("video")}>
            Video
          </button>
          <button data-active={tab === "library"} className="tab-btn" onClick={() => setTab("library")}>
            Library
          </button>
        </nav>
        <div className="ml-auto">
          <button className="btn-ghost text-xs" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-hidden">
        {tab === "chat" && <ChatTab onUseImagePrompt={jumpToImage} onUseVideoPrompt={(p) => jumpToVideo({ kind: "text", prompt: p })} onError={handleErr} />}
        {tab === "image" && <ImageTab initialPrompt={imagePromptSeed} onAnimate={(rec) => jumpToVideo({ kind: "image", prompt: rec.prompt, sourceImageId: rec.id, sourceImageUrl: rec.url })} onError={handleErr} />}
        {tab === "video" && <VideoTab seed={videoSeed} onClearSeed={() => setVideoSeed(null)} onExtend={(rec) => jumpToVideo({ kind: "extend", prompt: rec.prompt, sourceVideoId: rec.id, sourceVideoUrl: rec.url ?? undefined })} onError={handleErr} />}
        {tab === "library" && <LibraryTab onAnimate={(rec) => jumpToVideo({ kind: "image", prompt: rec.prompt, sourceImageId: rec.id, sourceImageUrl: rec.url })} onExtend={(rec) => jumpToVideo({ kind: "extend", prompt: rec.prompt, sourceVideoId: rec.id, sourceVideoUrl: rec.url ?? undefined })} onError={handleErr} />}
      </main>
    </div>
  );
}
